import {
  CARE_DATE_MONTH_NAME_PATTERN,
  countCareExplicitTimes,
  createCareTimelineHeadingRegex,
  normalizeCareDateTimeText,
} from '../../../shared/careDateTimePatterns';

const CAREGIVER_TIMELINE_MONTH_PATTERN = CARE_DATE_MONTH_NAME_PATTERN;
const CAREGIVER_TIMELINE_HEADING_PATTERN = createCareTimelineHeadingRegex({ start: true });
const CAREGIVER_TIMELINE_HEADING_GLOBAL_PATTERN = createCareTimelineHeadingRegex({ global: true });
const CAREGIVER_INLINE_STATUS_DATE_PATTERN = new RegExp(
  `\\b(?:stopped|started|increased|decreased)\\s+(?:on\\s+)?${CAREGIVER_TIMELINE_MONTH_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`,
  'i'
);
const CAREGIVER_INLINE_STATUS_DATE_GLOBAL_PATTERN = new RegExp(
  `\\b(?:stopped|started|increased|decreased)\\s+(?:on\\s+)?${CAREGIVER_TIMELINE_MONTH_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`,
  'ig'
);

const LONG_NOTE_GREETING_PATTERNS = [
  /^(hi|hello|dear|good morning|good afternoon|good evening)\b/i,
  /^hi everyone\b/i,
  /^hi team\b/i,
];

const LONG_NOTE_SIGNATURE_PATTERNS = [
  /^(thanks|thank you|regards|sincerely|warmly|best)\b/i,
  /^(?:thanks|thank you),?$/i,
];

const LONG_NOTE_SENDER_LINE_PATTERN = /^(?:[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2}|[A-Z][A-Za-z.-]+)\.?$/;
const LONG_NOTE_SECTION_PATTERNS = [
  /\b(?:food|breakfast|lunch|dinner|snack|behavior|mood|medication|sleep|activity|school|teacher|recess|art class)\b/i,
];

const normalizeNaturalCaptureChunkText = (value = '') => String(value || '')
  .replace(/\r\n/g, '\n')
  .trim();

const getNormalizedLine = (line = '') => String(line || '').trim();

const normalizeTimelineDetectionLine = (line = '') => normalizeCareDateTimeText(line);

const isBoilerplateLine = (line = '', index = 0, lines = []) => {
  const normalized = getNormalizedLine(line);
  if (!normalized) {
    return true;
  }

  if (LONG_NOTE_GREETING_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return true;
  }

  if (LONG_NOTE_SIGNATURE_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return true;
  }

  const isStandaloneName = LONG_NOTE_SENDER_LINE_PATTERN.test(normalized);
  if (isStandaloneName) {
    const lastNonEmptyIndex = lines.reduce((acc, current, currentIndex) => (
      getNormalizedLine(current) ? currentIndex : acc
    ), -1);
    const isNearEnd = lastNonEmptyIndex >= 0 && index >= lastNonEmptyIndex - 1;
    return isNearEnd;
  }

  return false;
};

export const looksLikeCaregiverTimelineImportText = (text = '') => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  if (!rawText) {
    return false;
  }

  const lines = rawText
    .split('\n')
    .map((line) => normalizeTimelineDetectionLine(line))
    .filter(Boolean);

  const dateSignalCount = lines.reduce((count, line) => (
    CAREGIVER_TIMELINE_HEADING_PATTERN.test(line) || CAREGIVER_INLINE_STATUS_DATE_PATTERN.test(line)
      ? count + 1
      : count
  ), 0);

  if (dateSignalCount >= 2) {
    return true;
  }

  const normalizedInlineText = lines.join(' ');
  const inlineHeadingCount = Array.from(
    normalizedInlineText.matchAll(CAREGIVER_TIMELINE_HEADING_GLOBAL_PATTERN)
  ).length;
  const inlineStatusCount = Array.from(
    normalizedInlineText.matchAll(CAREGIVER_INLINE_STATUS_DATE_GLOBAL_PATTERN)
  ).length;

  return inlineHeadingCount + inlineStatusCount >= 2;
};

export const isLongNoteEmailText = (text = '') => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  if (!rawText) {
    return false;
  }

  if (looksLikeCaregiverTimelineImportText(rawText)) {
    return false;
  }

  const lines = rawText.split('\n');
  const paragraphs = rawText.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
  const words = rawText.split(/\s+/).filter(Boolean);
  const hasGreetingOrSignature = lines.some((line) => (
    LONG_NOTE_GREETING_PATTERNS.some((pattern) => pattern.test(getNormalizedLine(line)))
    || LONG_NOTE_SIGNATURE_PATTERNS.some((pattern) => pattern.test(getNormalizedLine(line)))
    || LONG_NOTE_SENDER_LINE_PATTERN.test(getNormalizedLine(line))
  ));
  const hasMultiSectionLanguage = LONG_NOTE_SECTION_PATTERNS.some((pattern) => pattern.test(rawText))
    && paragraphs.length > 1;

  return Boolean(
    paragraphs.length > 1
    || hasGreetingOrSignature
    || words.length >= 90
    || rawText.length >= 450
    || hasMultiSectionLanguage
  );
};

export const shouldUseImportedNarrativeReviewText = (text = '') => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  if (!rawText) {
    return false;
  }

  return looksLikeCaregiverTimelineImportText(rawText)
    || isLongNoteEmailText(rawText);
};

export const shouldRerouteQuickCaptureNarrativeToImportReview = ({
  text = '',
  structuredEntryCount = 0,
  structuredEntries = [],
  usedBackendExtraction = false,
} = {}) => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  const timelineLikeText = looksLikeCaregiverTimelineImportText(rawText);
  const longEmailLikeText = isLongNoteEmailText(rawText);
  if (!(timelineLikeText || longEmailLikeText)) {
    return false;
  }

  const count = Number(structuredEntryCount) || 0;
  if (count === 0) {
    return true;
  }

  if (timelineLikeText && count <= 2) {
    return true;
  }

  const anchorCount = Array.from(
    rawText.matchAll(CAREGIVER_TIMELINE_HEADING_GLOBAL_PATTERN)
  ).length + Array.from(
    rawText.matchAll(CAREGIVER_INLINE_STATUS_DATE_GLOBAL_PATTERN)
  ).length;
  const explicitTimeCount = countCareExplicitTimes(rawText);
  const textIsLong = rawText.length >= 450;
  const textIsDateDense = anchorCount >= 3 || explicitTimeCount >= 3;
  const candidateEntries = Array.isArray(structuredEntries) ? structuredEntries : [];
  const candidateTexts = candidateEntries
    .map((entry) => String(
      entry?.body
      || entry?.text
      || entry?.note
      || entry?.description
      || entry?.summary
      || entry?.details
      || entry?.content
      || entry?.title
      || ''
    ).trim())
    .filter(Boolean);

  const averageEntryLength = candidateTexts.length > 0
    ? candidateTexts.reduce((sum, value) => sum + value.length, 0) / candidateTexts.length
    : 0;
  const hasDenseNarrativeRows = count >= 4 && averageEntryLength >= 80;
  const hasVerboseEntries = averageEntryLength >= 110
    || candidateTexts.some((value) => value.length >= 180);
  const hasBlobLikeEntries = candidateTexts.some((value) => {
    const rowAnchorCount = Array.from(value.matchAll(CAREGIVER_TIMELINE_HEADING_GLOBAL_PATTERN)).length
      + Array.from(value.matchAll(CAREGIVER_INLINE_STATUS_DATE_GLOBAL_PATTERN)).length;
    const rowTimeCount = countCareExplicitTimes(value);
    return rowAnchorCount >= 2 || rowTimeCount >= 2 || value.length >= 220;
  });

  if (textIsLong && textIsDateDense && (hasVerboseEntries || hasBlobLikeEntries)) {
    return true;
  }

  if (
    longEmailLikeText
    && (textIsLong || rawText.length >= 280 || candidateTexts.length >= 4)
    && count <= 4
    && (hasDenseNarrativeRows || hasVerboseEntries || hasBlobLikeEntries)
  ) {
    return true;
  }

  return timelineLikeText && !usedBackendExtraction && count <= 3;
};

export const resolveNaturalCapturePasteRouting = ({
  text = '',
  structuredEntries = [],
  structuredEntryCount = 0,
  usedBackendExtraction = false,
} = {}) => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  const narrativeImportLike = shouldUseImportedNarrativeReviewText(rawText);
  const shouldUseImportedNarrativeReview = shouldRerouteQuickCaptureNarrativeToImportReview({
    text: rawText,
    structuredEntries,
    structuredEntryCount,
    usedBackendExtraction,
  });

  return {
    narrativeImportLike,
    shouldUseImportedNarrativeReview,
    shouldShowNoLinesError: !narrativeImportLike,
  };
};

export const stripNaturalCaptureEmailBoilerplate = (text = '') => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  if (!rawText) {
    return '';
  }

  const lines = rawText.split('\n');
  const cleanedLines = lines.filter((line, index) => !isBoilerplateLine(line, index, lines));

  return cleanedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

export const splitNaturalCaptureLongNoteBlocks = (text = '') => {
  const rawText = normalizeNaturalCaptureChunkText(text);
  if (!rawText) {
    return [];
  }

  return rawText
    .split(/\n{2,}/)
    .map((block) => block.split('\n').map((line) => getNormalizedLine(line)).filter(Boolean).join(' '))
    .map((block) => block.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

export { normalizeNaturalCaptureChunkText };
